import { z } from "zod";

/**
 * Single recommended tool inside an Analyst {@link SolutionRecommendations}
 * output. Denormalises `slug` + `name` from the `tools` row so a DBA reading
 * `solution_presentations.recommendations` can identify the tool without a
 * join.
 *
 * `addresses_objective_indices` references positions in the parent memo's
 * `objectives[]` array. The Zod schema cannot bound that array length at
 * parse time (the memo isn't visible), so the Analyst runner does a
 * post-parse range check and clamps + audits any out-of-range entry before
 * insert.
 *
 * The trailing `match_validation_*` fields are added by the Match Validator
 * (#143) when it re-writes the parent `recommendations` jsonb. The Analyst
 * always leaves them undefined; downstream consumers (admin-UI, exporters)
 * must treat the recommendation as unvalidated when those fields are
 * absent. `match_validation_status: "removed"` does NOT strip the entry —
 * the rec stays in `tools[]` with its original Analyst-written fields
 * intact, marked-but-preserved so a reviewer can see what the Analyst
 * proposed alongside the validator's reasoning.
 */
const matchCheckOutcomeSchema = z.object({
  checkId: z.enum([
    "MV1-constraint-compliance",
    "MV2-score-integrity",
    "MV3-problem-relevance",
    "MV4-rationale-accuracy",
  ]),
  passed: z.boolean(),
  reason: z.string().min(1).max(500).nullable(),
});

export type MatchCheckOutcome = z.infer<typeof matchCheckOutcomeSchema>;

const recommendedToolSchema = z.object({
  toolId: z.string().uuid(),
  slug: z.string().min(1).max(120),
  name: z.string().min(1).max(200),
  objective_score: z.number().min(0).max(1),
  rationale: z.string().min(20).max(2000),
  addresses_objective_indices: z
    .array(z.number().int().nonnegative())
    .min(0),
  /**
   * Set by Match Validator (#143). 0–5 scale to match the m2-7-8 gate
   * (`average quality_score ≥ 3.0` across a curated set). Undefined
   * until the Match Validator has run.
   */
  match_validation_score: z.number().min(0).max(5).optional(),
  /**
   * Set by Match Validator. `"removed"` is a soft-tombstone: the entry
   * stays in `tools[]` so the Analyst's original case is preserved, but
   * downstream rendering and the HITL presentation must treat the entry
   * as ineligible for client delivery.
   */
  match_validation_status: z.enum(["kept", "removed"]).optional(),
  /**
   * Populated only when {@link match_validation_status} is `"removed"`.
   * One short sentence explaining the removal (constraint name + which
   * value clashed, or the LLM's relevance reason).
   *
   * **Denormalised echo, not source of truth.** This field is a copy
   * of the `reason` from whichever check in {@link match_check_outcomes}
   * triggered the removal (MV1 budget overrun or MV3 problem-relevance
   * failure). It exists so admin-UI renderers don't have to scan the
   * outcomes array to surface a one-line summary on the rec tile. If
   * the two ever diverge, `match_check_outcomes` wins — the validator
   * runner derives `removal_reason` from the outcomes, not the other
   * way around.
   */
  removal_reason: z.string().min(1).max(500).optional(),
  /**
   * Per-check audit trail. Includes both deterministic (MV1, MV2) and
   * LLM-judged (MV3, MV4) outcomes. Length 0..4 in practice; an empty
   * array means the validator did not run any per-rec check on this
   * entry (shouldn't happen in normal flow but tolerated for forward-
   * compat).
   */
  match_check_outcomes: z.array(matchCheckOutcomeSchema).optional(),
});

export type RecommendedTool = z.infer<typeof recommendedToolSchema>;

/**
 * Zod gate on `solution_presentations.recommendations` writes. Issue #191.
 *
 * The DB column is `jsonb` so storage accepts any shape; this schema is
 * the application-level guard that an LLM-produced shape mismatch throws a
 * typed `ZodError` rather than landing in the row. Mirrors the same pattern
 * `@prospect/messaging` uses to gate every Anthropic content block at the
 * provider boundary and `@prospect/google` uses to gate Sheets-read shapes.
 *
 * The discriminated union forces an either-or between assembly and custom
 * narrative — without it the LLM could emit both a populated `tools` array
 * and a narrative, leaving downstream consumers guessing.
 *
 * Discriminant values match the free-text vocabulary called out in the
 * `solutionPresentations.solutionType` column comment
 * (`packages/db/src/schema.ts`): `custom_build`, `tool_assembly`,
 * `hybrid`. All three are accepted because the runtime prompt is
 * DB-backed (CLAUDE.md invariant 11) — an operator editing the prompt
 * via the admin-UI Prompts tab can start emitting `hybrid` without a
 * code deploy, so the schema is the wider of "what the M2 prompt emits
 * today" and "what an admin-edited prompt could emit tomorrow." The M2
 * seed only emits `tool_assembly` and `custom_build`; `hybrid` is the
 * forward-compat slot for a tools-plus-narrative combination that a
 * later prompt revision can opt into.
 *
 * Numeric `objectives_coverage_pct` is a 0..1 fraction, not a percent —
 * same precision posture as `problem_memos.validation_score`.
 */
/**
 * List-level ranking-order verdict written by Match Validator (#143).
 * `passed: true` means the order across kept recommendations is sensible
 * given the memo's objectives; `false` flags the presentation for human
 * review without stripping anything. Undefined until Match Validator runs.
 *
 * Lives at the solution level (not per-rec) because the judgement is
 * comparative across the whole `tools[]` array — a single rec can't be
 * "wrongly ordered" by itself.
 */
const rankingOrderAssessmentSchema = z.object({
  passed: z.boolean(),
  reason: z.string().min(1).max(500).nullable(),
});

export type RankingOrderAssessment = z.infer<typeof rankingOrderAssessmentSchema>;

export const solutionRecommendationsSchema = z.discriminatedUnion(
  "solution_type",
  [
    z.object({
      solution_type: z.literal("tool_assembly"),
      objectives_coverage_pct: z.number().min(0).max(1),
      tools: z.array(recommendedToolSchema).min(1).max(6),
      custom_proposal_narrative: z.null(),
      ranking_order_assessment: rankingOrderAssessmentSchema.optional(),
    }),
    z.object({
      solution_type: z.literal("custom_build"),
      objectives_coverage_pct: z.number().min(0).max(1),
      tools: z.array(recommendedToolSchema).max(3),
      custom_proposal_narrative: z.string().min(200).max(20_000),
      ranking_order_assessment: rankingOrderAssessmentSchema.optional(),
    }),
    z.object({
      solution_type: z.literal("hybrid"),
      objectives_coverage_pct: z.number().min(0).max(1),
      tools: z.array(recommendedToolSchema).min(1).max(4),
      custom_proposal_narrative: z.string().min(200).max(20_000),
      ranking_order_assessment: rankingOrderAssessmentSchema.optional(),
    }),
  ],
);

export type SolutionRecommendations = z.infer<
  typeof solutionRecommendationsSchema
>;
